import { useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isNotEmpty } from '@lhb/func';
import store from '@/store/store';
import { UPDATE_TENANT_CHECK_INFO } from '@/store/common';
import { tenantCheck } from 'src/common/api/common';

/**
 * 获取当前租户的检查信息
 * @description
 * @return {*}
 * @demo
  const {
    getTenantCheckInfo,
    tenantCheckInfo,
    taskTemplateCode,
    isStandardATaskTemplate,
    isClusterTaskTemplate,
    isStandardAndClusterATaskTemplate,
    isStandardAndClusterBTaskTemplate,
    isCircleTaskTemplate,
    isSenmaTaskTemplate,
    isKwkTaskTemplate,
  } = useTenantCheckInfo();
 */
export const useTenantCheckInfo = ({ immediate }: {
  /** 是否立即调用接口获取一次 */
  immediate: boolean;
} = { immediate: true }) => {

  /** 获取当前租户的检查信息 */
  const tenantCheckInfo = useSelector((state: any) => state.common.tenantCheck) || null;

  /** 当前租户的拓店任务模板 */
  const taskTemplateCode = tenantCheckInfo?.taskTemplateCode || null;

  /** 更新当前租户的检查信息 */
  const updateTenantCheckInfo = useCallback((data) => {
    store.dispatch(UPDATE_TENANT_CHECK_INFO(data));
  }, []);

  /** 获取拓店任务模板
   * @description
   * @param {*} force 强制更新数据
   * @return {*}
   * @demo
   */
  const getTenantCheckInfo = useCallback(async (force?: boolean): Promise<string> => {
    // 如果已经有缓存，直接返回
    if (tenantCheckInfo && Object.keys(tenantCheckInfo).length && !force) {
      return tenantCheckInfo;
    }

    try {
      const res = await tenantCheck().catch(() => {});
      const tenantCheckInfo = res || {};

      // 更新租户检查信息
      updateTenantCheckInfo(tenantCheckInfo);
      return tenantCheckInfo;
    } catch (error) {
      console.error('获取拓店任务模板代码失败:', error);
      throw error;
    }
  }, [tenantCheckInfo, updateTenantCheckInfo]);

  /**
   * 当前租户的拓店任务模板是否加盟商模式A拓店任务
   * @description 未确定时为 null，确定后为 true/false
   * 相当于 旧的 isCircleTask
   */
  const isStandardATaskTemplate = useMemo(() => {
    if (!isNotEmpty(taskTemplateCode)) return null;
    return ['standardA'].includes(taskTemplateCode as string);
  }, [taskTemplateCode]);

  /**
   * 当前租户的拓店任务模板是否直营拓店任务
   * @description 未确定时为 null，确定后为 true/false
   * 相当于 旧的 isCircleTask
   */
  const isClusterTaskTemplate = useMemo(() => {
    if (!isNotEmpty(taskTemplateCode)) return null;
    return ['clusterA', 'clusterB'].includes(taskTemplateCode as string);
  }, [taskTemplateCode]);

  /**
   * 当前租户的拓店任务模板是否“加盟+直营A（基诺浦）”
   * @description 未确定时为 null，确定后为 true/false
   */
  const isStandardAndClusterATaskTemplate = useMemo(() => {
    if (!isNotEmpty(taskTemplateCode)) return null;
    return ['standardAndClusterA'].includes(taskTemplateCode as string);
  }, [taskTemplateCode]);

  /**
   * 当前租户的拓店任务模板是否“加盟+直营B（一鸣）”
   * @description 未确定时为 null，确定后为 true/false
   */
  const isStandardAndClusterBTaskTemplate = useMemo(() => {
    if (!isNotEmpty(taskTemplateCode)) return null;
    return ['standardAndClusterB'].includes(taskTemplateCode as string);
  }, [taskTemplateCode]);

  /**
   * 当前租户的拓店任务模板是否商圈拓店任务（指含有商圈的模板任务）
   * @description 未确定时为 null，确定后为 true/false
   * 不是 isCircleTask
   */
  const isCircleTaskTemplate = useMemo(() => {
    if (!isNotEmpty(taskTemplateCode)) return null;
    return ['standardAndClusterA', 'standardAndClusterB', 'clusterA', 'clusterB', 'clusterC'].includes(taskTemplateCode as string);
  }, [taskTemplateCode]);

  /**
   * 当前租户的拓店任务模板是否森马拓店任务
   * @description 未确定时为 null，确定后为 true/false
   */
  const isSenmaTaskTemplate = useMemo(() => {
    if (!isNotEmpty(taskTemplateCode)) return null;
    return ['clusterC'].includes(taskTemplateCode as string);
  }, [taskTemplateCode]);

  /**
   * 当前租户的拓店任务模板是否卡旺卡拓店任务
   * @description 未确定时为 null，确定后为 true/false
   */
  const isKwkTaskTemplate = useMemo(() => {
    if (!isNotEmpty(taskTemplateCode)) return null;
    return ['clusterB'].includes(taskTemplateCode as string);
  }, [taskTemplateCode]);

  /** 是否开通话术库 */
  const hasScriptLib = useMemo(() => {
    return tenantCheckInfo?.hasScriptLib === true;
  }, [tenantCheckInfo]);

  useEffect(() => {
    immediate && getTenantCheckInfo();
  }, []);

  return {
    // actions
    /** 获取当前租户的检查信息 */
    getTenantCheckInfo,

    // state
    /** 当前租户的检查信息 */
    tenantCheckInfo,
    /** 拓店任务指派归属类型 */
    taskAssignType: tenantCheckInfo?.taskAssignType,
    /* 当前租户的拓店任务模板 */
    taskTemplateCode,
    /** 是否加盟商模式A拓店任务，未确定时为null */
    isStandardATaskTemplate,
    /** 是否直营拓店任务，未确定时为null */
    isClusterTaskTemplate,
    /** 当前租户的拓店任务模板是否“加盟+直营A（基诺浦）”，未确定时为null */
    isStandardAndClusterATaskTemplate,
    /** 当前租户的拓店任务模板是否“加盟+直营B（一鸣）”，未确定时为null */
    isStandardAndClusterBTaskTemplate,
    /** 是否商圈拓店任务（指含有商圈的模板任务），未确定时为null */
    isCircleTaskTemplate,
    /** 是否森马拓店任务，未确定时为null */
    isSenmaTaskTemplate,
    /** 是否卡旺卡拓店任务，未确定时为null */
    isKwkTaskTemplate,
    /** 是否开通话术库 */
    hasScriptLib,
  };
};
